import type { FC } from 'react';
import { useMemo } from 'react';

import { muiTheme, theme } from '../../styles/theme';

export interface DesignCounts {
  'Clinical trial': number;
  'Implementation study': number;
  'Regional programme': number;
  'National programme': number;
}

interface DonutClusterMarkerProps {
  designCounts: DesignCounts;
  totalCount: number;
  size?: number;
}

const DESIGN_COLORS: Record<keyof DesignCounts, string> = {
  'Clinical trial': theme.colors['blue-study'],
  'Implementation study': theme.colors['pale-orange'],
  'Regional programme': theme.colors['yellow-100'],
  'National programme': theme.colors['green-100'],
};

const DESIGN_ORDER: (keyof DesignCounts)[] = [
  'Clinical trial',
  'Implementation study',
  'Regional programme',
  'National programme',
];

/**
 * Generate SVG path for a donut arc segment
 * @param centerX - Center X coordinate
 * @param centerY - Center Y coordinate
 * @param radius - Outer radius of the donut
 * @param innerRadius - Inner radius of the donut (creates the hole)
 * @param startAngle - Start angle in degrees (0 = top, clockwise)
 * @param endAngle - End angle in degrees
 */
function createArcPath(
  centerX: number,
  centerY: number,
  radius: number,
  innerRadius: number,
  startAngle: number,
  endAngle: number,
): string {
  // Convert degrees to radians (0° = top = -90° in standard coordinates)
  const startRad = ((startAngle - 90) * Math.PI) / 180;
  const endRad = ((endAngle - 90) * Math.PI) / 180;

  // Calculate outer arc points
  const x1 = centerX + radius * Math.cos(startRad);
  const y1 = centerY + radius * Math.sin(startRad);
  const x2 = centerX + radius * Math.cos(endRad);
  const y2 = centerY + radius * Math.sin(endRad);

  // Calculate inner arc points
  const x3 = centerX + innerRadius * Math.cos(endRad);
  const y3 = centerY + innerRadius * Math.sin(endRad);
  const x4 = centerX + innerRadius * Math.cos(startRad);
  const y4 = centerY + innerRadius * Math.sin(startRad);

  // Determine if we need the large arc flag
  const largeArcFlag = endAngle - startAngle > 180 ? 1 : 0;

  // Build the path:
  // M = move to start point
  // A = arc to end point (outer arc)
  // L = line to inner arc start
  // A = arc back (inner arc, reversed)
  // Z = close path
  return [
    `M ${x1} ${y1}`,
    `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
    `L ${x3} ${y3}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${x4} ${y4}`,
    'Z',
  ].join(' ');
}

function getClusterSize(pointCount: number, baseSize = 60): number {
  if (pointCount < 10) return baseSize * 0.67; // 40px
  if (pointCount < 25) return baseSize * 0.75; // 45px
  if (pointCount < 50) return baseSize * 0.83; // 50px
  if (pointCount < 100) return baseSize * 0.92; // 55px
  if (pointCount < 200) return baseSize; // 60px
  return baseSize * 1.17; // 70px
}

export const DonutClusterMarker: FC<DonutClusterMarkerProps> = ({
  designCounts,
  totalCount,
  size,
}) => {
  const diameter = size ?? getClusterSize(totalCount);
  const radius = diameter / 2;
  const donutWidth = diameter * 0.2; // Donut thickness (20% of diameter)
  const innerRadius = radius - donutWidth;

  // Clamp font size between 11px and 16px
  const fontSize = Math.min(Math.max(diameter * 0.3, 10), 12);

  const segments = useMemo(() => {
    const result: {
      design: keyof DesignCounts;
      count: number;
      percentage: number;
      startAngle: number;
      endAngle: number;
      color: string;
    }[] = [];

    let currentAngle = 0;

    for (const design of DESIGN_ORDER) {
      const count = designCounts[design] || 0;
      if (!count) continue;

      const percentage = count / totalCount;
      const angle = percentage * 360;

      // Clamp final segment so we never exceed 360 due to rounding
      const endAngle = Math.min(currentAngle + angle, 360);

      result.push({
        design,
        count,
        percentage: percentage * 100,
        startAngle: currentAngle,
        endAngle,
        color: DESIGN_COLORS[design],
      });

      currentAngle = endAngle;
    }

    // Optional: ensure the last segment ends exactly at 360 if total rounding < 360
    if (result.length > 0 && result[result.length - 1].endAngle < 359.999) {
      result[result.length - 1].endAngle = 360;
    }

    return result;
  }, [designCounts, totalCount]);

  // Handle single design type (360° arc doesn't render properly, use full circle instead)
  const hasOnlyOneDesign = segments.length === 1;

  return (
    <svg
      role="img"
      aria-label={`Cluster of ${totalCount} studies: ${segments
        .map(s => `${s.design} (${Math.round(s.percentage)}%)`)
        .join(', ')}`}
      width={diameter}
      height={diameter}
      viewBox={`0 0 ${diameter} ${diameter}`}
      style={{
        cursor: 'pointer',
      }}
    >
      {/* Donut segments or full circle */}
      {hasOnlyOneDesign ? (
        // Single design: render as a full donut ring
        <>
          <circle
            cx={radius}
            cy={radius}
            r={radius - donutWidth / 2}
            fill="none"
            stroke={segments[0].color}
            strokeWidth={donutWidth}
          />
        </>
      ) : (
        // Multiple designs: render as segments
        segments.map(segment => (
          <path
            key={segment.design}
            d={createArcPath(
              radius,
              radius,
              radius,
              innerRadius,
              segment.startAngle,
              segment.endAngle,
            )}
            fill={segment.color}
          />
        ))
      )}

      {/* Center circle (white background for count) */}
      <circle cx={radius} cy={radius} r={innerRadius} fill="white" />

      {/* Total count text */}
      <text
        x={radius}
        y={radius}
        textAnchor="middle"
        dominantBaseline="central"
        fill={muiTheme.palette.text.primary}
        fontSize={fontSize}
        fontFamily={theme.fontFamilies.default}
      >
        {totalCount}
      </text>
    </svg>
  );
};
